import forEach from 'lodash/forEach';
import isEmpty from 'lodash/isEmpty';
import some from 'lodash/some';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  ButtonGroup,
  ErrorLoad,
  Interpolate,
  MessageWrap,
  Paginator,
  StepCSSTransitionGroup,
  Table,
  TableBody,
  Toggle,
} from '@wg/wows-react-uikit';

import { root } from '~/preloaded';
import settings from '~/settings';
import { REQUESTS_TABS } from '~/constants';
import { isChatDenied } from '~/helpers/account';
import { t } from '~/helpers/localization';
import useMount from '~/hooks/useMount';
import {
  ClanIsDisbandedTooltip,
  ClanMembersInfoMissingTooltip,
  MemberInfoMissingTooltip,
  MemberIsBannedTooltip,
  RankTooltipContent,
} from '~/tooltips';
import { openAccountProfile, openChatWindow } from '~/web2ClientAPI/base';

import ApplicationsClanMemberTableItem from '~/Components/Requests/Applications/ApplicationsClanMemberTableItem';
import InvitesNonClanMemberTableItem from '~/Components/Requests/Invites/InvitesNonClanMemberTableItem';
import TooltipManager from '~/Components/UIKit/components/Tooltip/TooltipManager';
import { EntriesTableHead } from '~/UIKit';
import { Tooltip } from '~/UIKit/components';

import styles from './Entries.scss';
import Sticky from '../Sticky/Sticky';
import StickyContainer from '../Sticky/StickyContainer';

import type { NavigateFunction } from 'react-router-dom';
import type { IClanApplication } from '~/Actions/ActionApplications';
import type { ClanInfoType } from '~/Actions/ActionClanProfile';
import type { IAccountInvite, IInvite } from '~/Actions/ActionInvites';
import type { IApplicationsState } from '~/Reducers/ReducerApplications';
import type { IInvitesState } from '~/Reducers/ReducerInvites';
import type { ICurrentAccountState } from '~/store/slices/currentAccountSlice';
import type { ClanPreloaded } from '~/types/declaration';

interface EntriesHeaderItem {
  text: string;
  isSortable: boolean;
  name: string;
  modify: string;
  glyph?: string;
}

type CommonProps = {
  currentAccount: ICurrentAccountState;
  headers: EntriesHeaderItem[];
  navigate: NavigateFunction;

  dropSelection?: () => void;
  fetchEntriesByCurrentState: (withGlobalSpinner: boolean) => void;
  fetchEntriesByPage: (page: number) => void;

  onReloadClick: () => void;
  onSortClick: (order: string, isAsc: boolean) => void;
};

type InvitesProps = CommonProps & {
  entryType: REQUESTS_TABS.INVITES;
  entries: IInvitesState;

  onAcceptedEntry: (entry: IAccountInvite) => void;
  onDeclinedEntry: (entry: IAccountInvite) => void;

  onRulesDialogClick: () => void;

  selectedApplicationsPlayersIds?: never;
  onAllApplicationsPlayersTick?: never;
  onApplicationsPlayerTick?: never;
  onDeclineApplications?: never;
};

type ApplicationsProps = CommonProps & {
  clan: ClanInfoType | ClanPreloaded;
  entryType: REQUESTS_TABS.APPLICATION;
  entries: IApplicationsState;

  onAcceptedEntry: (entry: IClanApplication) => void;
  onDeclinedEntry: (entry: IClanApplication) => void;

  selectedApplicationsPlayersIds: number[];
  onAllApplicationsPlayersTick?: (playerIds: number[]) => void;
  onApplicationsPlayerTick?: (playerId: number) => void;
  onDeclineApplications?: () => void;

  onRulesDialogClick?: never;
};

type OperationalEntriesProps = InvitesProps | ApplicationsProps;

const OperationalEntries = (props: OperationalEntriesProps) => {
  const { currentAccount, entries, entryType, headers, navigate, dropSelection } = props;

  const onApplicationsPlayerTick =
    props.entryType === REQUESTS_TABS.APPLICATION ? props.onApplicationsPlayerTick : undefined;

  const appRef = useRef<HTMLElement | null>(null);

  const [entryListIds, setEntryListIds] = useState<number[]>([]);
  const [fetchByNewPage, setFetchByNewPage] = useState(false);

  const selectedApplicationsPlayersIds = useMemo(() => {
    return props.entryType === REQUESTS_TABS.APPLICATION ? props.selectedApplicationsPlayersIds : [];
  }, [props.entryType, props.selectedApplicationsPlayersIds]);

  const withGlobalSpinner = useCallback(() => {
    return entries.error === null && isEmpty(entries[entryType]) && isEmpty(entries.meta);
  }, [entries, entryType]);

  useMount(() => {
    appRef.current = document.getElementById('app');

    props.fetchEntriesByCurrentState(withGlobalSpinner());

    if (appRef.current) {
      appRef.current.scrollTop = 0;
    }
  });

  useEffect(() => {
    return () => {
      dropSelection?.();
    };
  }, [dropSelection]);

  useEffect(() => {
    const nextIds: number[] = [];
    forEach(entries[entryType] as (IInvite | IClanApplication)[], (value: IInvite | IClanApplication) => {
      nextIds.push(value.id);
    });

    setEntryListIds(nextIds);
  }, [entries, entryType]);

  // Track previous page
  const prevPageRef = useRef(entries.page);
  useEffect(() => {
    setFetchByNewPage(prevPageRef.current !== entries.page);
    prevPageRef.current = entries.page;
  }, [entries.page]);

  // Scroll after local fetching
  const prevFetchingRef = useRef(entries.isFetching);
  const prevErrorRef = useRef(entries.error);

  useEffect(() => {
    const wasFetching = prevFetchingRef.current;
    const wasError = prevErrorRef.current;

    const isScrollAfterLocalFetching = wasFetching && !entries.isFetching && wasError === null;

    if (isScrollAfterLocalFetching && appRef.current) {
      appRef.current.scrollTop = 0;
    }

    prevFetchingRef.current = entries.isFetching;
    prevErrorRef.current = entries.error;
  }, [entries.isFetching, entries.error]);

  const onDeclineApplicationsClick = useCallback(() => {
    if (props.entryType === REQUESTS_TABS.APPLICATION) {
      void props.onDeclineApplications?.();
    }
  }, [props]);

  const onAllApplicationsPlayersTickClick = useCallback(() => {
    if (props.entryType === REQUESTS_TABS.APPLICATION) {
      props.onAllApplicationsPlayersTick?.(entryListIds);
    }
  }, [entryListIds, props]);

  const renderError = useCallback(() => {
    const message =
      entryType === REQUESTS_TABS.INVITES
        ? t('Не удалось получить информацию по приглашениям.')
        : t('Не удалось получить информацию по заявкам.');

    return <ErrorLoad isFlat message={message} onReloadClick={props.onReloadClick} />;
  }, [entryType, props.onReloadClick]);

  const renderEntries = useCallback(() => {
    if (entryType === REQUESTS_TABS.INVITES) {
      const invites = entries[entryType] as IAccountInvite[];

      const onAcceptedInvite = (invite: IAccountInvite) => {
        TooltipManager.destroyActiveTooltip();
        props.onAcceptedEntry(invite);
      };

      return (
        <TableBody>
          {invites.map((invite) => (
            <InvitesNonClanMemberTableItem
              key={invite.id}
              invite={invite}
              isInProcessCancelation={some(entries.idsToProcess, (id) => id === invite.id)}
              root={root}
              navigate={navigate}
              currentAccount={currentAccount}
              onAcceptedInvite={onAcceptedInvite}
              onDeclinedInvite={props.onDeclinedEntry}
            />
          ))}
        </TableBody>
      );
    }

    const applications = entries[entryType] as IClanApplication[];

    const onAcceptedApplication = (application: IClanApplication) => {
      TooltipManager.destroyActiveTooltip();
      props.onAcceptedEntry(application);
    };

    return (
      <TableBody>
        {applications.map((application) => (
          <ApplicationsClanMemberTableItem
            key={application.id}
            isChatDenied={isChatDenied(currentAccount, application.account)}
            isChecked={selectedApplicationsPlayersIds.indexOf(application.id) !== -1}
            application={application}
            clan={props.clan}
            isProcessing={some(entries.idsToProcess, (id) => id === application.id)}
            onAcceptedApplication={onAcceptedApplication}
            onDeclinedApplication={props.onDeclinedEntry}
            openAccountProfile={openAccountProfile}
            openChatWindow={openChatWindow}
            onApplicationsPlayerTick={onApplicationsPlayerTick}
          />
        ))}
      </TableBody>
    );
  }, [currentAccount, entries, entryType, navigate, onApplicationsPlayerTick, props, selectedApplicationsPlayersIds]);

  const renderPaginator = useCallback(() => {
    if (entries.meta) {
      return (
        <Paginator
          page={entries.page}
          total={entries.meta.total || 1}
          limit={settings.entries.limit}
          isFetching={fetchByNewPage}
          isDisabled={entries.isFetching}
          onSelectedPage={props.fetchEntriesByPage}
        />
      );
    }

    return null;
  }, [entries.isFetching, entries.meta, entries.page, fetchByNewPage, props.fetchEntriesByPage]);

  const renderClanRules = useCallback(() => {
    if (entryType !== REQUESTS_TABS.INVITES) {
      return null;
    }

    const clanRulesComponent = (
      <Toggle
        caption={t('Правилами кланов')}
        onClick={() => {
          props.onRulesDialogClick();
        }}
      />
    );

    return (
      <div key="rules" className={styles.rules}>
        <Interpolate str={t('Вступая в клан, вы соглашаетесь с %(clanRules)s')} clanRules={clanRulesComponent} />
      </div>
    );
  }, [entryType, props]);

  const renderStickyMenu = useCallback(() => {
    if (entryType !== REQUESTS_TABS.APPLICATION) {
      return null;
    }

    const isActive = props.selectedApplicationsPlayersIds.length > 0;

    return (
      <Sticky
        usedStickyContainerId="operational-entries-table-control-block-sticky-container"
        isActive={isActive}
        forBottom
      >
        <div className={styles.isHidden}>{t('Принять выбранные')}</div>
        <div className={styles.usersControlBlock}>
          <ButtonGroup>
            <Button
              isFlat
              isDisabled={!props.selectedApplicationsPlayersIds.length}
              onClick={onDeclineApplicationsClick}
            >
              {t('Отклонить выбранные')}
            </Button>
          </ButtonGroup>
        </div>
      </Sticky>
    );
  }, [entryType, onDeclineApplicationsClick, props]);

  const isGlobalFetching = entries.isFetching;

  const total = entries.meta?.total || 0;
  const isErrored = entries.error !== null;

  const allPlayersIsChecked = useMemo(() => {
    if (entryType !== REQUESTS_TABS.APPLICATION) {
      return null;
    }

    return entries[entryType].length === props.selectedApplicationsPlayersIds.length;
  }, [entries, entryType, props]);

  const getRankTooltipContentCb = useCallback(
    (tooltipTip: React.ReactNode) => {
      if (!tooltipTip) {
        return null;
      }

      const memberId = Number(tooltipTip);
      const list = entries[entryType] as IClanApplication[];
      const entry = list.find((e) => e.account.id === memberId);

      if (!entry) {
        return null;
      }

      return (
        <RankTooltipContent
          rank={entry.statistics.rank}
          seasonId={entry.statistics.season_id}
          seasonRank={entry.statistics.season_rank}
        />
      );
    },
    [entries, entryType],
  );

  if (isGlobalFetching) {
    return null;
  }

  if (!isErrored && total === 0 && !entries.isFetching) {
    let title = t('Нет актуальных заявок');
    let message = t('Заявок на вступление в ваш клан пока нет.');

    if (entryType === REQUESTS_TABS.INVITES) {
      title = t('Нет актуальных приглашений');
      message = t('У вас пока нет приглашений в клан.');
    }

    return (
      <StepCSSTransitionGroup level={1}>
        <MessageWrap title={title} message={message} key="operational-message-empty" />
      </StepCSSTransitionGroup>
    );
  }

  return (
    <StepCSSTransitionGroup level={1}>
      <StickyContainer key="operational-entries-table" id="operational-entries-table-control-block-sticky-container">
        <Table
          key="table"
          isFetching={entries.isFetching}
          isErrored={isErrored}
          stickyContainerId="operational-entries-sticky-container"
        >
          <EntriesTableHead
            entryType={entryType}
            sort={entries.sort}
            headers={headers}
            stickyContainerId="operational-entries-sticky-container"
            allPlayersIsChecked={allPlayersIsChecked}
            onAllApplicationsPlayersTick={onAllApplicationsPlayersTickClick}
            onSortClick={props.onSortClick}
          />
          {isErrored ? renderError() : renderEntries()}
        </Table>

        {renderStickyMenu()}
      </StickyContainer>

      {renderClanRules()}
      {isErrored ? null : renderPaginator()}

      <ClanIsDisbandedTooltip />
      <ClanMembersInfoMissingTooltip />
      <MemberIsBannedTooltip />
      <MemberInfoMissingTooltip />

      <Tooltip id="rank-tooltip" getContent={getRankTooltipContentCb} />
    </StepCSSTransitionGroup>
  );
};

export default memo(OperationalEntries);
